import React, { useState, useCallback } from 'react';
import {getStepFromPosition} from './getStepFromPosition.js';
import {getBarWidth} from './getBarWidth.js';
import {quantizeStep} from './quantizeStep.js';
import {DEFAULT_RESOLUTION} from './constants.js';
import { connect } from "react-redux";
import { setArrangementSelection } from "actions/index.js";
import { setArrangementRowSelections } from "actions/index.js";
import { setArrangementLoopSelected } from "actions/index.js";
import {NO_SUB_SEQUENCE} from 'session/constants.js';

const RIGHT = 'RIGHT';
const LEFT = 'LEFT';

function SelectionComponent(props) {
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState(undefined);
    const [directionResizing, setDirectionResizing] = useState(undefined);
    const [didDrag, setDidDrag] = useState(false);

    const onMouseDown = useCallback((e) => {
        e.stopPropagation();
        setIsDragging(true);
        setDidDrag(false);
        let step = getStepFromPosition(getMouseX(e), props.resolution, undefined, props.xScale);
        let offset = step - props.selectionStart;
        setDragOffset(offset);
    }, [
        setIsDragging,
        setDidDrag,
        isDragging,
        props.selectionStart,
        props.resolution,
        setDragOffset,
        props.xScale]);

    const onMiddleMouseUp = useCallback((e) => {
        if (!didDrag) {
            props.setArrangementLoopSelected(!props.loopSelected);
            if (!props.loopSelected) {
                selectAll(props.selectionStart, props.selectionEnd);
            } else {
                unselectAll();
            }
        }
    }, [didDrag, props.loopSelected]);

    const getMouseX = useCallback((e) => {
        if (props.arrangementContainer) {
            return props.arrangementContainer.scrollLeft + e.pageX;
        } else {
            return e.pageX;
        }
    }, [props.arrangementContainer]);

    const onMouseUp = useCallback((e) => {
        setIsDragging(false);
        setDragOffset(undefined);
        setDirectionResizing(undefined);
        setDidDrag(false);
    }, [setIsDragging, setDragOffset, setDidDrag]);

    const onMouseMove = useCallback((e) => {
        if (isDragging) {
            let newStart = getStepFromPosition(getMouseX(e), props.resolution, undefined, props.xScale) - dragOffset;
            if (newStart < 0) {
                return;
            }
            let selectionDur = props.selectionEnd - props.selectionStart;
            let newEnd = newStart + selectionDur;
            let quantizedStart = quantizeStep(newStart);
            let quantizedEnd = quantizeStep(newEnd);
            props.setArrangementSelection(
                quantizedStart, 
                quantizedEnd); 
            if (props.loopSelected) {
                selectAll(quantizedStart, quantizedEnd);
            }
            setDidDrag(true);
        } else if (directionResizing === LEFT) {
            let newStart = quantizeStep(
                getStepFromPosition(getMouseX(e), props.resolution, undefined, props.xScale));
            if (newStart >= props.selectionEnd) {
                return;
            }
            props.setArrangementSelection(
                newStart,
                props.selectionEnd);
            if (props.loopSelected) {
                selectAll(newStart, props.selectionEnd);
            }
            setDidDrag(true);
        } else if (directionResizing === RIGHT) {
            let newEnd = quantizeStep(
                getStepFromPosition(getMouseX(e), props.resolution, undefined, props.xScale));
            if (newEnd <= props.selectionStart) {
                return;
            }
            props.setArrangementSelection(
                props.selectionStart,
                newEnd);
            setDidDrag(true);
            if (props.loopSelected) {
                selectAll(props.selectionStart, newEnd);
            }
        } 
    }, [props.selectionStart, setDidDrag,
        props.selectionEnd, isDragging,
        props.loopSelected, props.xScale,
        props.resolution, dragOffset, directionResizing]);

    const unselectAll = () => {
        props.setArrangementRowSelections({});
    };

    const selectAll = (start, end) => {
        let selection = {start, end};
        let rowSelections = {};
        rowSelections[NO_SUB_SEQUENCE.id] = selection;
        for (let i in props.sequencerEngine.subSequences) {
            rowSelections[props.sequencerEngine.subSequences[i].id] = selection;
        }

        props.setArrangementRowSelections(rowSelections);
    };

    const onEdgeDown = useCallback((e, direction) => {
        setDirectionResizing(direction);
    }, [setDirectionResizing]);
        

    let className = props.loopSelected ? 'selection looping' : 'selection';
    return (
        <div
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
            className="selection-container">
          <div>
            <div style={getSelectionStyle(props)} className={className}>
              <div
                onMouseDown={(e) => onEdgeDown(e, LEFT)}
                className="triangle-right"/>
                <div
                  onMouseDown={onMouseDown}
                  onMouseUp={onMiddleMouseUp}
                  className="middle"/>
                <div
                  onMouseDown={(e) => onEdgeDown(e, RIGHT)}
                  className="triangle-left"/>
                </div>
              </div>
            </div>
    );
}

function getAbsoluteSelection(props) {
    let style = {
        left: getBarWidth(props.selectionStart, props.resolution, props.xScale),
        width: getBarWidth(props.selectionEnd - props.selectionStart, props.resolution, props.xScale),
    };

    let className = "absolute-selection";
    if (props.loopSelected) {
        className += ' looping'
    }
    return (
        <div className={className} style={style}>
        </div>
    );
}

function getSelectionStyle(props) {
    return {
        marginLeft: getBarWidth(props.selectionStart, props.resolution, props.xScale),
        width: getBarWidth(props.selectionEnd - props.selectionStart, props.resolution, props.xScale),
    };
}

const mapStateToProps = state => {
    let selection = state.arrangement.selection;
    return {
        selectionStart: selection !== undefined ? selection.start : undefined,
        selectionEnd: selection !== undefined ? selection.end : undefined,
        loopSelected: state.arrangement.loopSelected,
        resolution: DEFAULT_RESOLUTION,
        xScale: state.arrangement.xScale
    };
};

 const mapDispatchToProps = dispatch => {
    return {
        setArrangementRowSelections: (selections) => dispatch(setArrangementRowSelections(selections)),
        setArrangementSelection: (start, end) => dispatch(setArrangementSelection(start, end)),
        setArrangementLoopSelected: (selected) => dispatch(setArrangementLoopSelected(selected))
    };
};
export default connect(mapStateToProps, mapDispatchToProps)(SelectionComponent);
